/**
 * rotahq.uk marketing landing page.
 *
 * The public homepage and the destination for Google Ads / Meta Ads traffic.
 * Logged-in users never see this — App.js redirects them to their dashboard.
 *
 * SEO is managed here (title / description / canonical / JSON-LD) because
 * this is an SPA: tags are applied on mount and restored on unmount so the
 * rest of the app keeps its own metadata.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { MarketingHeader } from '@/components/landing/MarketingHeader';
import { HeroSection } from '@/components/landing/HeroSection';
import { ProblemSection } from '@/components/landing/ProblemSection';
import { BenefitsSection } from '@/components/landing/BenefitsSection';
import { FeaturesSection } from '@/components/landing/FeaturesSection';
import { ShowcaseSection } from '@/components/landing/ShowcaseSection';
import { HowItWorksSection } from '@/components/landing/HowItWorksSection';
import { PricingSection } from '@/components/landing/PricingSection';
import { TrustSection } from '@/components/landing/TrustSection';
import { FaqSection, FAQ_ITEMS } from '@/components/landing/FaqSection';
import { FinalCtaSection, MarketingFooter } from '@/components/landing/FinalCtaSection';
import { DemoDialog } from '@/components/landing/DemoDialog';
import { InstallPrompt } from '@/components/common/InstallPrompt';
import { marketingEvent } from '@/lib/marketing';

const CANONICAL_URL = 'https://rotahq.uk/';
const PAGE_TITLE = 'Hospitality Staff Scheduling Software UK | RotaHQ';
const PAGE_DESCRIPTION =
  'Simplify staff rotas with RotaHQ, the hospitality workforce management software. Manage restaurant shifts with ease. Start your free 14-day trial.';

/** Truthful structured data only — no ratings, reviews or invented claims.
 * Shape follows the agency schema deliverable (6-Oct-26): an @id-linked
 * graph of Organization / WebSite / WebPage / Service / BreadcrumbList,
 * plus the pre-existing SoftwareApplication (pricing offers) and FAQPage
 * blocks which the agency schema does not replace. */
function buildJsonLd() {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': 'https://rotahq.uk/#organization',
      name: 'RotaHQ',
      url: CANONICAL_URL,
      logo: {
        '@type': 'ImageObject',
        '@id': 'https://rotahq.uk/#logo',
        url: 'https://rotahq.uk/icon-512.png',
        contentUrl: 'https://rotahq.uk/icon-512.png',
        width: 512,
        height: 512,
      },
      description: 'RotaHQ provides restaurant staff scheduling, attendance and payroll software for UK restaurants.',
      email: 'htet@rotahq.uk',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'High Street',
        addressLocality: 'Romford',
        addressRegion: 'Greater London',
        postalCode: 'RM1 1JU',
        addressCountry: 'GB',
      },
      sameAs: [
        'https://www.facebook.com/rotahq/',
        'https://www.instagram.com/rotahq/',
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': 'https://rotahq.uk/#website',
      name: 'RotaHQ',
      url: CANONICAL_URL,
      description: PAGE_DESCRIPTION,
      publisher: { '@id': 'https://rotahq.uk/#organization' },
      inLanguage: 'en-GB',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': 'https://rotahq.uk/#webpage',
      url: CANONICAL_URL,
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      isPartOf: { '@id': 'https://rotahq.uk/#website' },
      about: { '@id': 'https://rotahq.uk/#service' },
      publisher: { '@id': 'https://rotahq.uk/#organization' },
      breadcrumb: { '@id': 'https://rotahq.uk/#breadcrumb' },
      primaryImageOfPage: { '@id': 'https://rotahq.uk/#logo' },
      inLanguage: 'en-GB',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://rotahq.uk/#service',
      name: 'RotaHQ Restaurant Staff Scheduling & Payroll Software',
      url: CANONICAL_URL,
      serviceType: 'Restaurant Workforce Management Software',
      description: 'Restaurant staff scheduling with RotaHQ, UK hospitality workforce management software for rotas, attendance and payroll in one place.',
      provider: { '@id': 'https://rotahq.uk/#organization' },
      areaServed: { '@type': 'Country', name: 'United Kingdom' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': 'https://rotahq.uk/#breadcrumb',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: CANONICAL_URL },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'RotaHQ',
      description: PAGE_DESCRIPTION,
      url: CANONICAL_URL,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web browser',
      publisher: { '@id': 'https://rotahq.uk/#organization' },
      offers: [
        { '@type': 'Offer', name: 'Starter', price: '19.00', priceCurrency: 'GBP' },
        { '@type': 'Offer', name: 'Pro', price: '49.00', priceCurrency: 'GBP' },
        { '@type': 'Offer', name: 'Business', price: '149.00', priceCurrency: 'GBP' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    },
  ];
}

function useLandingSeo() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;

    const metaDescription = document.querySelector('meta[name="description"]');
    const prevDescription = metaDescription?.getAttribute('content');
    metaDescription?.setAttribute('content', PAGE_DESCRIPTION);

    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = CANONICAL_URL;
    canonical.setAttribute('data-landing-seo', '1');
    document.head.appendChild(canonical);

    const jsonLd = document.createElement('script');
    jsonLd.type = 'application/ld+json';
    jsonLd.setAttribute('data-landing-seo', '1');
    jsonLd.textContent = JSON.stringify(buildJsonLd());
    document.head.appendChild(jsonLd);

    return () => {
      document.title = prevTitle;
      if (prevDescription) metaDescription?.setAttribute('content', prevDescription);
      document.querySelectorAll('[data-landing-seo="1"]').forEach((el) => el.remove());
    };
  }, []);
}

/** Fires view_pricing exactly once when the pricing section becomes visible. */
function usePricingViewEvent() {
  const firedRef = useRef(false);
  useEffect(() => {
    const el = document.getElementById('pricing');
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (firedRef.current) return;
        if (entries.some((entry) => entry.isIntersecting)) {
          firedRef.current = true;
          marketingEvent('view_pricing');
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
}

export default function LandingPage() {
  const [demoOpen, setDemoOpen] = useState(false);

  useLandingSeo();
  usePricingViewEvent();

  const onBookDemo = useCallback((placement) => {
    marketingEvent('click_book_demo', { placement });
    setDemoOpen(true);
  }, []);

  return (
    <div className="bg-[color:var(--brand-bg)] text-[color:var(--brand-text)]" data-testid="marketing-landing-page">
      <MarketingHeader onBookDemo={onBookDemo} />
      <main>
        <HeroSection onBookDemo={onBookDemo} />
        <ProblemSection />
        <BenefitsSection />
        <FeaturesSection />
        <ShowcaseSection />
        <HowItWorksSection onBookDemo={onBookDemo} />
        <PricingSection />
        <TrustSection />
        <FaqSection />
        <FinalCtaSection onBookDemo={onBookDemo} />
      </main>
      <MarketingFooter />
      <DemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
      <InstallPrompt testIdPrefix="landing-pwa" />
    </div>
  );
}
